import { useEffect, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui';
import { NotificationProgressIndicator } from '@/features/lb-notifications/lb-notifications.components';
import { cn } from '@/lib/utils';

// The run's length is unknown, so the bar eases toward this share and fills on done
const RUNNING_PROGRESS = 90;

type CardRunToastProps = {
  readonly label: string;
  readonly title?: string;
  readonly done: boolean;
  // Takes back what the user did, such as ticking a to-do
  readonly onUndo?: () => void;
};

// The toast that follows a card's run, with the bar the meeting notifications use
export const CardRunToast = ({
  label,
  title,
  done,
  onUndo,
}: CardRunToastProps) => {
  // Starts empty and widens on the next frame, so the width transition runs
  const [started, setStarted] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setStarted(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  const progress = done ? 100 : started ? RUNNING_PROGRESS : 0;

  return (
    <span
      role="status"
      className="relative isolate flex w-[var(--width)] items-center gap-2 overflow-hidden rounded-lg border border-border-strong bg-surface px-4 py-3 text-sm text-foreground shadow-lg"
    >
      {done ? (
        <Icons.check aria-hidden="true" className="size-4 shrink-0" />
      ) : null}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-medium">{label}</span>
        {title ? (
          <span className="truncate text-xs text-foreground-subtle">
            {title}
          </span>
        ) : null}
      </span>
      {onUndo ? (
        <Button variant="outline" size="dense" onClick={onUndo}>
          <Icons.undo aria-hidden="true" />
          Undo
        </Button>
      ) : null}
      <NotificationProgressIndicator
        progress={progress}
        className={cn(
          'transition-[width] ease-out',
          done ? 'duration-300' : 'duration-[8000ms]'
        )}
      />
    </span>
  );
};
